import React, { useState, useEffect, useCallback } from 'react';
import { bountyService } from '../api/services';
import '../pages/Bounty.css';

const ROLE_LABEL = { OWNER: '負責人', COLLABORATOR: '協作者' };

// 懸賞點數分配視窗：負責人或管理者把懸賞點數以「絕對點數」分配給自己與協作者，總和需等於懸賞點數。
// 結案時後端依此草稿發放；未設定時預設全給負責人（向後相容）。
const BountyAllocationModal = ({ bountyId, open, onClose, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [bountyPoints, setBountyPoints] = useState(0);
  const [rows, setRows] = useState([]); // [{ user_id, name, role, points }]

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await bountyService.getAllocation(bountyId);
      setBountyPoints(res.data?.bountyPoints || 0);
      setRows((res.data?.participants || []).map((p) => ({ ...p, points: Number(p.points) || 0 })));
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [bountyId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  if (!open) return null;

  const total = rows.reduce((s, r) => s + (Number(r.points) || 0), 0);
  const balanced = total === bountyPoints;

  // 設定單一參與者點數（收斂為非負整數）
  const setPoints = (userId, val) => {
    const n = Math.max(0, Math.floor(Number(val) || 0));
    setRows((prev) => prev.map((r) => (r.user_id === userId ? { ...r, points: n } : r)));
  };

  // 平均分配：盡量平均，餘數加到第一位（通常為負責人）
  const splitEvenly = () => {
    const n = rows.length;
    if (!n) return;
    const base = Math.floor(bountyPoints / n);
    const remainder = bountyPoints - base * n;
    setRows((prev) => prev.map((r, i) => ({ ...r, points: base + (i === 0 ? remainder : 0) })));
  };

  // 全給負責人（重設為預設）
  const allToOwner = () => {
    setRows((prev) => prev.map((r) => ({ ...r, points: r.role === 'OWNER' ? bountyPoints : 0 })));
  };

  const handleSave = async () => {
    if (!balanced) { setError(`分配總和需等於 ${bountyPoints} 點`); return; }
    setBusy(true);
    setError('');
    try {
      await bountyService.setAllocation(bountyId, rows.map((r) => ({ user_id: r.user_id, points: Number(r.points) || 0 })));
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setBusy(false);
    }
  };

  return (
    <div className="bounty-modal-overlay" onClick={onClose}>
      <div className="bounty-modal" onClick={(e) => e.stopPropagation()}>
        <div className="bounty-modal-header"><h3>分配點數</h3></div>
        {error && <div className="alert alert-danger">{error}</div>}
        {loading ? (
          <p className="confirm-message">載入中...</p>
        ) : rows.length === 0 ? (
          <p className="confirm-message">目前沒有可分配的參與者。</p>
        ) : (
          <>
            <p className="confirm-message">
              將懸賞的 <strong>{bountyPoints}</strong> 點分配給負責人與協作者，結案時依此發放。未調整時預設全給負責人。
            </p>
            <div className="bounty-alloc-tools">
              <button type="button" className="btn btn-sm btn-secondary" onClick={splitEvenly}>平均分配</button>
              <button type="button" className="btn btn-sm btn-secondary" onClick={allToOwner}>全給負責人</button>
            </div>
            <ul className="bounty-alloc-list">
              {rows.map((r) => (
                <li key={r.user_id} className="bounty-alloc-row">
                  <span className="bounty-alloc-name">
                    <span className={`badge ${r.role === 'OWNER' ? 'badge-me' : 'badge-collab'}`}>{ROLE_LABEL[r.role] || r.role}</span>
                    <span className="bounty-alloc-who" style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{r.name}</span>
                  </span>
                  <input
                    type="number"
                    min="0"
                    className="form-control form-control-sm bounty-alloc-input"
                    value={r.points}
                    onChange={(e) => setPoints(r.user_id, e.target.value)}
                  />
                </li>
              ))}
            </ul>
            <div className={`bounty-alloc-total ${balanced ? 'ok' : 'bad'}`}>
              合計 {total} / {bountyPoints} 點
              {balanced ? ' ✓' : (total > bountyPoints ? `（超出 ${total - bountyPoints} 點）` : `（尚差 ${bountyPoints - total} 點）`)}
            </div>
          </>
        )}
        <div className="bounty-modal-footer">
          <span />
          <div>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>取消</button>
            <button type="button" className="btn btn-primary" onClick={handleSave} disabled={busy || loading || !balanced || rows.length === 0}>儲存分配</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BountyAllocationModal;
